import React, { useState, useMemo } from 'react'
import { Formik } from 'formik'
import { useSelector } from 'react-redux'
import { QRCodeSVG } from 'qrcode.react'
import * as Yup from 'yup'
import { Button, Typography } from '../../../../components'
import { Form, FormBody, FormFooter } from '../../../../components/Form/styles'
import { signUpActions } from '../../../../redux/ducks/signUp'
import { PaymentMethod } from '../../../../constants/order'
import { SuccessMessage } from '../../../../components/Form/SuccessMessage'
import { formatCentsToReal } from '../../../../helpers/formatCentsToReal'
import { useValidateCoupon } from '../helpers'
import ErrorMessage from '../../../../components/Form/ErrorMessage'
import useActions from '../../../../hooks/useActions'
import Input from '../../../../components/Form/Input'
import copyToClipboard from '../../../../utils/copyToClipboard'
import LocalStorageService from '../../../../services/LocalStorageService'

import { InputContainer } from '../../styles'
import * as S from './styles'

/*
expiresAt: "2024-01-04T22:09:53Z"
qrCode: "00020101021226820014br.gov.bcb.pix2560pix.stone.com.br/pix/v2/91b96eaf-7ec0-4636-95da-b4949491b9795204000053039865406129.005802BR5925ECBR Fomento ao E-Commerc6014RIO DE JANEIRO622905251a47d7d18e444b8ea1c3ac319630482EB"
qrCodeUrl: "https://api.pagar.me/core/v5/transactions/tran_paqlj38sxLhEyP6d/qrcode?payment_method=pix"
transactionType: "pix"
*/

const PixForm = () => {
  const user = LocalStorageService.get('user') || {}
  const { setStep4, nextStep, prevStep, pay, payWithExistingUser, setError } =
    useActions(signUpActions)
  const { error, step3, step4, step5 } = useSelector((state) => state.signUp)
  const [clipboardBtnText, setClipboardBtnText] = useState('Copiar Código Pix')
  const [discountPercentage, setDiscountPercentage] = React.useState(0)

  const { success, pix } = step5?.purchase?.order ?? {}
  const { productSelected } = step3

  const formattedAmount = useMemo(
    () =>
      formatCentsToReal(
        productSelected.amount - productSelected.amount * discountPercentage
      ),
    [productSelected.amount, discountPercentage]
  )

  const onSubmit = async ({ coupon, ..._values }, { setSubmitting }) => {
    setSubmitting(true)

    await setStep4({
      ...step4,
      coupon
    })

    // If the user is not empty, it means that the user is already logged in and have an account. So, it means that the payment is to buy another certificate for another brand of the same user company.
    if (user?.id) {
      await payWithExistingUser(PaymentMethod.PIX, user)
    } else {
      await pay(PaymentMethod.PIX)
    }

    setSubmitting(false)
  }

  const handleClickCopyToClipboard = () => {
    setClipboardBtnText('Copiado!')
    copyToClipboard(pix?.qrCode)
    setTimeout(() => {
      setClipboardBtnText('Copiar Código Pix')
    }, 2000)
  }

  const handleValidateCoupon = useValidateCoupon(
    setDiscountPercentage,
    setError,
    discountPercentage
  )

  const formSchema = useMemo(
    () =>
      Yup.object().shape({
        coupon: Yup.string()
          .optional()
          .nullable()
          .test('validateCoupon', 'Cupom inválido', handleValidateCoupon)
      }),
    [handleValidateCoupon]
  )

  const initialValues = {
    coupon: ''
  }

  return (
    <InputContainer>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={formSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <FormBody type="flex" centered>
              {success ? (
                <>
                  <Typography
                    type="text"
                    color="#7D7987"
                    spacing="0 0 16px 0"
                    align="center"
                    weight="bold"
                  >
                    Sua compra foi realizada e será processada após o pagamento
                    do Pix
                  </Typography>
                </>
              ) : (
                <>
                  <Typography type="text" color="#7D7987" spacing="0 0 16px 0">
                    Pagamento com Pix
                  </Typography>
                  <Input
                    name="coupon"
                    label="Cupom de desconto"
                    showValidIcon
                    textTransform="uppercase"
                  />
                  {discountPercentage > 0 && (
                    <SuccessMessage>
                      {`Cupom de desconto aplicado: ${
                        discountPercentage * 100
                      }%`}
                    </SuccessMessage>
                  )}
                </>
              )}
              <S.BoxCardBankSlip>
                <S.ValueContainer>
                  <Typography type="text" color="#151515">
                    Valor Total:
                  </Typography>
                  <Typography type="text" color="#151515">
                    {discountPercentage > 0 ? (
                      <>
                        de{' '}
                        <strike>
                          {formatCentsToReal(productSelected.amount)}
                        </strike>{' '}
                        por <strong>{formattedAmount}</strong>
                      </>
                    ) : (
                      formattedAmount
                    )}
                  </Typography>
                </S.ValueContainer>
                {!pix?.qrCode && (
                  <>
                    <Typography
                      type="subTitle"
                      color="#7D7987"
                      spacing="16px 0 0"
                    >
                      Clique no botão &quot;Gerar Pix&quot; para gerar o QR Code
                      de pagamento
                    </Typography>
                  </>
                )}
              </S.BoxCardBankSlip>
              {pix?.qrCode && (
                <>
                  <S.QRCodeInfoContainer>
                    <Typography
                      type="text"
                      color="#7D7987"
                      spacing="0 0 16px 0"
                    >
                      Como proceder?
                    </Typography>

                    <Typography
                      type="subTitle"
                      color="#7D7987"
                      spacing="0 0 8px 0"
                    >
                      <strong>QR Code:</strong>
                    </Typography>
                    <Typography type="subTitle" color="#7D7987">
                      - Abra o aplicativo do seu banco, entre no ambiente{' '}
                      <strong>Pix</strong>
                    </Typography>
                    <Typography type="subTitle" color="#7D7987">
                      - Escolha a opção de pagar com <strong>QR Code</strong> e
                      escaneie o QR Code abaixo com o seu celular
                    </Typography>
                    <Typography
                      type="subTitle"
                      color="#7D7987"
                      spacing="0 0 16px 0"
                    >
                      - Confirme as informações e finalize o pagamento
                    </Typography>

                    <S.QRCodeContainer>
                      <QRCodeSVG
                        value={pix?.qrCode}
                        size={230}
                        bgColor="#fff"
                        fgColor="#000"
                      />
                    </S.QRCodeContainer>

                    <Typography
                      type="subTitle"
                      color="#7D7987"
                      spacing="16px 0"
                    >
                      <strong>Pix copia e cola:</strong>
                    </Typography>
                    <Typography type="subTitle" color="#7D7987">
                      - Copie o <strong>Código Pix</strong> usando o botão
                      abaixo
                    </Typography>
                    <Typography type="subTitle" color="#7D7987">
                      - Abra o aplicativo do seu banco, entre no ambiente{' '}
                      <strong>Pix</strong>
                    </Typography>
                    <Typography type="subTitle" color="#7D7987">
                      - Cole o código Pix no campo correspondente
                    </Typography>
                    <Typography
                      type="subTitle"
                      color="#7D7987"
                      spacing="0 0 16px 0"
                    >
                      - Confirme as informações e finalize o pagamento
                    </Typography>
                  </S.QRCodeInfoContainer>
                </>
              )}
              {error && <ErrorMessage width="100%">{error}</ErrorMessage>}
            </FormBody>
            <FormFooter>
              {success ? (
                <>
                  <Button
                    text={clipboardBtnText}
                    type="button"
                    variant="primary"
                    onClick={handleClickCopyToClipboard}
                    width="200px"
                    disabled={isSubmitting}
                  />
                  <Button
                    text="FINALIZAR"
                    type="button"
                    variant="outlined"
                    onClick={() => nextStep()}
                    width="200px"
                    disabled={isSubmitting}
                  />
                </>
              ) : (
                <>
                  <Button
                    text="VOLTAR"
                    type="button"
                    variant="outlined"
                    width="200px"
                    onClick={prevStep}
                  />
                  <Button
                    text="GERAR PIX"
                    type="submit"
                    variant="primary"
                    width="200px"
                    disabled={isSubmitting}
                  />
                </>
              )}
            </FormFooter>
          </Form>
        )}
      </Formik>
    </InputContainer>
  )
}

export default PixForm
